import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Pagination,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "react-toastify";
import "../../../css/master-common.css";

import {
  clearIncomeExpenseError,
  fetchExpenseListThunk,
  approveExpenseThunk,
  revokeExpenseThunk,
} from "../../../store/slices/incomeExpenseSlice";

const StatusBadge = ({ status }) => {
  const s = String(status || "").toLowerCase();

  return (
    <Badge className="mm-pill">
      {s === "approved" ? "Approved" : "Pending"}
    </Badge>
  );
};

// ✅ Convert
// "YYYY-MM-DD"
// "YYYY-MM-DD HH:mm:ss"
// "YYYY-MM-DDTHH:mm:ss.sssZ"
// into "DD-MM-YYYY"
const formatDMY = (value) => {
  if (!value) return "-";

  const s = String(value).trim();
  if (!s) return "-";

  const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
  const parts = datePart.split("-");

  if (parts.length !== 3) return s;

  const [y, m, d] = parts;
  if (!y || !m || !d) return s;

  return `${d}-${m}-${y}`;
};

const safeAmount = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
};

export default function ExpenseListPage() {
  const dispatch = useDispatch();

  const {
    expenseList = [],
    expensePagination,
    listLoading,
    error,
  } = useSelector((s) => s.incomeExpense || {});

  const user = useSelector((s) => s.auth?.user || s.auth?.profile || null);
  const role = String(user?.role || user?.user_type || "").toLowerCase();

  const isApprover = useMemo(() => {
    return role === "admin" || role === "user" || role === "superadmin";
  }, [role]);

  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [actingId, setActingId] = useState(null);

  const totalPages = Number(expensePagination?.totalPages || 1);
  const currentPage = Number(expensePagination?.page || page || 1);
  const pageLimit = Number(expensePagination?.limit || limit || 10);
  const startIndex = (currentPage - 1) * pageLimit;

  const loadList = (p = currentPage, l = pageLimit, s = statusFilter) => {
    dispatch(fetchExpenseListThunk({ status: s, page: p, limit: l }));
  };

  useEffect(() => {
    loadList(page, limit, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, statusFilter, page, limit]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, limit]);

  useEffect(() => {
    if (!listLoading && currentPage > 1 && (expenseList || []).length === 0) {
      setPage((p) => Math.max(1, p - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listLoading, currentPage, expenseList?.length]);

  const onApprove = async (expense_id) => {
    if (!expense_id) return;

    setActingId(expense_id);
    const r = await dispatch(approveExpenseThunk(expense_id));
    setActingId(null);

    if (approveExpenseThunk.fulfilled.match(r)) {
      toast.success("Approved ✅");
      loadList(currentPage, pageLimit, statusFilter);
    } else {
      toast.error(r?.payload || "Approve failed");
    }
  };

  const onRevoke = async (expense_id) => {
    if (!expense_id) return;

    setActingId(expense_id);
    const r = await dispatch(revokeExpenseThunk(expense_id));
    setActingId(null);

    if (revokeExpenseThunk.fulfilled.match(r)) {
      toast.info("Revoked to Pending ↩️");
      loadList(currentPage, pageLimit, statusFilter);
    } else {
      toast.error(r?.payload || "Revoke failed");
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;

    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      />
    );

    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPage(1)}>
          1
        </Pagination.Item>
      );
      if (start > 2) items.push(<Pagination.Ellipsis key="es" />);
    }

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="ee" />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    );

    return <Pagination className="mb-0 master-pagination">{items}</Pagination>;
  };

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h4 className="mb-1 fw-bold mm-title">Expense List</h4>
          {/* <div className="mm-subtitle">Approve / Revoke expenses</div> */}
        </div>

        <div className="d-flex gap-2 align-items-center flex-wrap">
          <div style={{ minWidth: 220 }}>
            <Form.Select
              className="mm-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
            </Form.Select>
          </div>

          <div style={{ minWidth: 160 }}>
            <Form.Select
              className="mm-select"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value || 10))}
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </Form.Select>
          </div>
        </div>
      </div>

      {error ? (
        <Alert
          variant="danger"
          className="mm-card-soft mb-3"
          dismissible
          onClose={() => dispatch(clearIncomeExpenseError())}
        >
          <div className="fw-bold mb-1">Error</div>
          <div>{String(error)}</div>
        </Alert>
      ) : null}

      <Card className="mm-card">
        <Card.Body className="p-3">
          {listLoading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" animation="border" />
              <div>Loading...</div>
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table className="mm-table align-middle" hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>#</th>
                      <th style={{ width: 140, whiteSpace: "nowrap" }}>Date</th>
                      <th>Expense Type</th>
                      <th style={{ width: 140, whiteSpace: "nowrap" }}>Amount</th>
                      <th style={{ width: 120 }}>Status</th>
                      <th style={{ width: 160, whiteSpace: "nowrap" }}>Approved By</th>
                      <th
                        className="mm-actions-th"
                        style={{ width: 170, whiteSpace: "nowrap" }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {(expenseList || []).length ? (
                      expenseList.map((x, idx) => {
                        const status =
                          x.approval_status || (x.approved ? "approved" : "pending");
                        const isApproved =
                          String(status).toLowerCase() === "approved";
                        const rowLoading = actingId === x.expense_id;

                        return (
                          <tr key={x.expense_id || idx}>
                            <td>{startIndex + idx + 1}</td>

                            <td style={{ whiteSpace: "nowrap" }}>
                              {formatDMY(x.date)}
                            </td>

                            <td>{x.expense_type_name || x.expense_type_id || "-"}</td>

                            <td style={{ whiteSpace: "nowrap" }}>
                              ₹ {safeAmount(x.amount)}
                            </td>

                            <td>
                              <StatusBadge status={status} />
                            </td>

                            <td style={{ whiteSpace: "nowrap" }}>
                              {x.approved_by_name ?? x.approved_by ?? "-"}
                            </td>

                            <td className="mm-actions-td">
                              {!isApprover ? (
                                <span className="text-muted">No access</span>
                              ) : (
                                <div className="d-flex gap-2">
                                  {!isApproved ? (
                                    <Button
                                      size="sm"
                                      variant="success"
                                      disabled={rowLoading}
                                      onClick={() => onApprove(x.expense_id)}
                                    >
                                      {rowLoading ? "..." : "Approve"}
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="warning"
                                      disabled={rowLoading}
                                      onClick={() => onRevoke(x.expense_id)}
                                    >
                                      {rowLoading ? "..." : "Revoke"}
                                    </Button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          No records
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="text-muted small">
                    Page <b>{currentPage}</b> / <b>{totalPages}</b>
                    {typeof expensePagination?.total !== "undefined" ? (
                      <>
                        {" "}
                        • Total: <b>{expensePagination.total}</b>
                      </>
                    ) : null}
                  </div>
                  {renderPagination()}
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}